/**
 * @file order-columns.tsx
 * @description Колонки реестра заказов для `ResponsiveList` (phase19.31, зона
 * «Заказы», макет (а)).
 *
 * Одно описание — две раскладки:
 *  - от 1024 таблица: Заказ · Стадия · Клиент · Оплата · Доставка · Ход ·
 *    Сумма; строка в две линии, вся строка — ссылка, без «›»;
 *  - на 1024–1279 (колонка содержимого ~700–950px) «Доставка» сворачивается
 *    второй линией в «Оплату», а «Ход» — в «Клиента» (`fold`); подписи этих
 *    ячеек на такой ширине прячутся — строка остаётся в две линии;
 *  - до 1024 компактный список: «номер … сумма» / «стадия · клиент ·
 *    телефон» / «проблема или ход · оплата · доставка». Номер — целиком:
 *    пилюля стадии на первой линии оставляла ему на 360 ~90px, и номер
 *    резался до «Z-2026-000…» (приёмка qa4 п. 1) — стадия ушла второй линией.
 * Номер заказа — обычным шрифтом с табличными цифрами; моноширинным только
 * трек-номер (SC43). Деньги — «1 175,00 ₽».
 */

import { OrderPaymentCell, OrderStageNote } from "./OrderRegistryCells";
import { OrderStagePill } from "../OrderStagePill";
import { OwnerChip } from "../OwnerChip";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import type { OrderDto } from "../../../lib/api-orders";
import { ownerMeta } from "../../../lib/order-chain-owner";
import {
  ORDER_SOURCE_SHORT,
  orderDeliveryCell,
  orderMoney,
  orderPaymentCell,
  orderShortDate,
  orderStageNote,
} from "../../../lib/order-registry-cells";
import { formatOrderPhone, isLegalEntityOrder } from "../../../lib/order-summary-lines";

/** Клиент второй линией: телефон и пометка «Юр. лицо». */
function clientSecondLine(order: OrderDto): string | null {
  const parts = [formatOrderPhone(order.customerPhone), isLegalEntityOrder(order) ? "Юр. лицо" : null];
  const text = parts.filter(Boolean).join(" · ");
  return text || null;
}

/** Доставка одной строкой — для свёрнутой колонки и телефона. */
function deliveryOneLine(order: OrderDto) {
  const cell = orderDeliveryCell(order);
  return (
    <>
      {cell.primary}
      {cell.track ? <span className="font-mono"> · {cell.track}</span> : null}
    </>
  );
}

/** «Чей ход · ответственный» одной строкой — для свёрнутой колонки «Ход». */
function ownerOneLine(order: OrderDto): string {
  const owner = order.ownerNow !== undefined ? ownerMeta(order.ownerNow).label : null;
  return [owner, order.ownerDisplayName ?? "не назначен"].filter(Boolean).join(" · ");
}

export const ORDER_REGISTRY_COLUMNS: ListColumn<OrderDto>[] = [
  {
    key: "order",
    header: "Заказ",
    width: "w-[144px]",
    cell: (o) => (
      <TwoLineCell
        primary={<span className="whitespace-nowrap font-medium tabular-nums">{o.number}</span>}
        secondary={`${ORDER_SOURCE_SHORT[o.source]} · ${orderShortDate(o.createdAt)}`}
      />
    ),
    compact: "title",
    compactCell: (o) => <span className="tabular-nums">{o.number}</span>,
  },
  {
    key: "stage",
    header: "Стадия",
    width: "w-[148px]",
    cell: (o) => <TwoLineCell primary={<OrderStagePill order={o} />} secondary={<OrderStageNote order={o} />} />,
    // Вторая линия, первой — перед клиентом (порядок колонок задаёт порядок).
    compact: "subtitle",
    compactCell: (o) => <OrderStagePill order={o} className="max-w-[9rem] align-middle" />,
  },
  {
    key: "client",
    header: "Клиент",
    cell: (o) => (
      <TwoLineCell
        primary={<span className="font-medium">{o.customerDisplayName ?? "Без имени"}</span>}
        // На 1024–1279 вторую линию занимает свёрнутый «Ход»; телефон — с 1280.
        secondary={<span className="hidden xl:inline">{clientSecondLine(o)}</span>}
      />
    ),
    compact: "subtitle",
    compactCell: (o) => [o.customerDisplayName ?? "Без имени", clientSecondLine(o)].filter(Boolean).join(" · "),
  },
  {
    // Телефон: третья линия начинается с причины остановки или «чей ход»
    // (в таблице это уже есть в «Стадии» и «Ходе»).
    key: "why",
    header: "Ход",
    compactOnly: true,
    cell: (o) => {
      const note = orderStageNote(o);
      if (note?.problem) return <OrderStageNote order={o} />;
      if (o.ownerNow === undefined) return null;
      const label = ownerMeta(o.ownerNow).label;
      return o.ownerNow === "MANAGER" ? label : `Ход: ${label.toLowerCase()}`;
    },
    compact: "meta",
  },
  {
    key: "payment",
    header: "Оплата",
    width: "w-[116px]",
    cell: (o) => <OrderPaymentCell order={o} />,
    compact: "meta",
    compactCell: (o) => orderPaymentCell(o).primary,
  },
  {
    key: "delivery",
    header: "Доставка",
    width: "w-[140px]",
    cell: (o) => {
      const cell = orderDeliveryCell(o);
      return (
        <TwoLineCell
          primary={cell.primary}
          secondary={cell.track ? <span className="font-mono">{cell.track}</span> : cell.status}
        />
      );
    },
    fold: { into: "payment", cell: deliveryOneLine },
    compact: "meta",
    compactCell: (o) => orderDeliveryCell(o).primary,
  },
  {
    key: "owner",
    header: "Ход",
    width: "w-[132px]",
    cell: (o) => (
      <TwoLineCell
        primary={o.ownerNow !== undefined ? <OwnerChip owner={o.ownerNow} /> : "—"}
        secondary={o.ownerDisplayName ?? "Не назначен"}
      />
    ),
    fold: { into: "client", cell: ownerOneLine },
  },
  {
    key: "sum",
    header: "Сумма",
    width: "w-[108px]",
    align: "end",
    cell: (o) => (
      <span className="flex flex-col items-end">
        <span className="type-body whitespace-nowrap font-semibold tabular-nums text-on-surface">{orderMoney(o)}</span>
        <span className="type-caption text-on-surface-variant tabular-nums">{o.itemsCount} поз.</span>
      </span>
    ),
    compact: "trailing",
    compactCell: (o) => <span className="whitespace-nowrap font-semibold tabular-nums">{orderMoney(o)}</span>,
  },
];
